import { useState, type FormEvent } from 'react';
import {
  Alert, AlertTitle, Box, Button, Card, CardActionArea, CardContent, CardMedia,
  CircularProgress, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { ApiFallo, TitleKind } from '../lib/api.js';
import { InsigniaBiblioteca } from '../components/InsigniaBiblioteca.js';

/** La ruta de la ficha según lo que sea (D-27): la misma pantalla, distinta dirección. */
export function rutaFicha(kind: TitleKind, id: string): string {
  return `/${kind === 'show' ? 'serie' : 'pelicula'}/${id}`;
}

export function Buscar() {
  // La consulta vive en la URL: así se puede enlazar aquí desde cualquier sitio —la
  // watchlist lo hace— y la búsqueda sobrevive a recargar o compartir el enlace.
  const [params, setParams] = useSearchParams();
  const consulta = params.get('q')?.trim() ?? '';
  // Películas o series (D-27), también en la URL. Películas por defecto: es lo que había.
  const kind: TitleKind = params.get('tipo') === 'series' ? 'show' : 'movie';
  const [texto, setTexto] = useState(consulta);

  const { data, isFetching, error } = useQuery({
    queryKey: ['buscar', consulta, kind],
    queryFn: () => api.buscar(consulta, kind),
    enabled: consulta.length > 0,
  });

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    const q = texto.trim();
    setParams(q ? { q, ...(kind === 'show' ? { tipo: 'series' } : {}) } : {}, { replace: true });
  };

  // Cambiar de pestaña repite la búsqueda que hubiera: una serie buscada en Películas no
  // sale, y volver a teclearla es el paso que sobra.
  const cambiarTipo = (nuevo: TitleKind | null) => {
    if (!nuevo || nuevo === kind) return;
    const q = texto.trim() || consulta;
    setParams({ ...(q ? { q } : {}), ...(nuevo === 'show' ? { tipo: 'series' } : {}) }, { replace: true });
  };

  const fallo = error as ApiFallo | null;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
        {/* Películas y series aparte (D-27): el filtro lo aplica HispaShare, no se criba
            aquí lo que ya vino mezclado. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={kind}
          onChange={(_, v: TitleKind | null) => cambiarTipo(v)}
          aria-label="Qué buscar"
        >
          <ToggleButton value="movie">Películas</ToggleButton>
          <ToggleButton value="show">Series</ToggleButton>
        </ToggleButtonGroup>
        <Box component="form" onSubmit={enviar} sx={{ display: 'flex', gap: 1, flex: 1, maxWidth: 620 }}>
          <TextField
            fullWidth size="small" autoFocus
            placeholder={kind === 'show' ? 'Buscar una serie…' : 'Buscar una película…'}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <Button type="submit" variant="contained" disabled={!texto.trim()}>
            Buscar
          </Button>
        </Box>
      </Stack>

      {data?.libraryAge && (
        <Typography variant="caption" color="text.secondary">
          Estado de la biblioteca según la sincronización de{' '}
          {new Date(data.libraryAge).toLocaleString('es-ES')}
        </Typography>
      )}

      {isFetching && <CircularProgress size={28} />}

      {/* Un error NUNCA se pinta como "no hay resultados": es la diferencia entre
          encogerse de hombros y saber que hay algo roto. */}
      {fallo && (
        <Alert severity={fallo.code === 'indexer_unconfigured' ? 'warning' : 'error'}>
          <AlertTitle>
            {fallo.code === 'indexer_unconfigured'
              ? 'HispaShare no está configurado'
              : 'La búsqueda ha fallado'}
          </AlertTitle>
          {fallo.message}
          {fallo.code !== 'indexer_unconfigured' && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Esto no significa que la película no exista: significa que no hemos podido
              preguntarlo.
            </Typography>
          )}
        </Alert>
      )}

      {!fallo && data && data.items.length === 0 && (
        <Alert severity="info">
          Sin resultados para «{consulta}» en {kind === 'show' ? 'series' : 'películas'}. La
          búsqueda funcionó; simplemente no hay nada con ese nombre.
          {' '}¿Estará en {kind === 'show' ? 'películas' : 'series'}?
        </Alert>
      )}

      <Grid container spacing={2}>
        {data?.items.map((r) => (
          <Grid key={r.id} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
            <Card sx={{ height: '100%' }}>
              <CardActionArea component={Link} to={rutaFicha(r.kind, r.id)} sx={{ height: '100%' }}>
                {r.posterUrl && (
                  <CardMedia
                    component="img"
                    image={r.posterUrl.startsWith('http') ? r.posterUrl : `https://www.hispashare.org/${r.posterUrl}`}
                    alt=""
                    sx={{ aspectRatio: '2/3', objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                    {r.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[r.year, r.director].filter(Boolean).join(' · ')}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <InsigniaBiblioteca estado={r.library} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
